/**
 * ProposalComments — inline client ↔ advisor comment thread anchored to a
 * proposal target (section / day / hotel / smart_question).
 * (PI-3 · Pass 2 P1 backlog · slice a)
 *
 * Two modes:
 *   - mode="client": leaves comments via /api/client-portal/trips/{tid}/proposal-comments
 *   - mode="advisor": full CRUD + convert-to-action via /api/design-studio
 *
 * Usage (client side):
 *   <ProposalComments mode="client" tripId={tripId} targetKind="section"
 *     targetId={s.key} targetLabel={titleFor(s.key)} />
 *
 * Usage (advisor side):
 *   <ProposalComments mode="advisor" tripId={tripId} />   (renders the full thread)
 */
import React from "react";
import { MessageSquare, Send, Check, Trash2, Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, EditorialRule } from "../index";

const TARGET_KIND_LABEL = {
  section: "Section",
  day: "Day",
  hotel: "Hotel",
  smart_question: "Smart confirmation",
  general: "General",
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function ProposalComments({
  mode = "client",
  tripId,
  targetKind,
  targetId,
  targetLabel,
  testIdBase = "proposal-comments",
}) {
  const advisor = mode === "advisor";
  const baseUrl = advisor
    ? `/design-studio/${tripId}/proposal-comments`
    : `/client-portal/trips/${tripId}/proposal-comments`;

  const [comments, setComments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [body, setBody] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [busyId, setBusyId] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(baseUrl);
      setComments(data?.comments || []);
    } catch {
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  React.useEffect(() => {
    load();
  }, [load]);

  // For non-advisor scoped views, only show comments tied to this target.
  const visible = React.useMemo(() => {
    if (advisor) return comments;
    if (!targetKind) return comments;
    return comments.filter(
      (c) =>
        c.target_kind === targetKind && (c.target_id || null) === (targetId || null)
    );
  }, [comments, advisor, targetKind, targetId]);

  async function submit() {
    const txt = body.trim();
    if (!txt) return;
    setSubmitting(true);
    try {
      const payload = {
        body: txt,
        target_kind: targetKind || "general",
        target_id: targetId || null,
        target_label: targetLabel || null,
      };
      await api.post(baseUrl, payload);
      setBody("");
      await load();
      toast.success("Comment shared.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save comment.");
    } finally {
      setSubmitting(false);
    }
  }

  async function resolve(id) {
    setBusyId(id);
    try {
      await api.patch(`${baseUrl}/${id}`, { status: "resolved" });
      await load();
    } catch {
      toast.error("Couldn't resolve.");
    } finally {
      setBusyId(null);
    }
  }

  async function reopen(id) {
    setBusyId(id);
    try {
      await api.patch(`${baseUrl}/${id}`, { status: "open" });
      await load();
    } catch {
      toast.error("Couldn't reopen.");
    } finally {
      setBusyId(null);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this comment?")) return;
    setBusyId(id);
    try {
      await api.delete(`${baseUrl}/${id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete.");
    } finally {
      setBusyId(null);
    }
  }

  async function convert(id) {
    setBusyId(id);
    try {
      await api.post(`${baseUrl}/${id}/convert-to-action`);
      await load();
      toast.success("Smart Action created.");
    } catch {
      toast.error("Couldn't convert.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div
      className="space-y-3"
      data-testid={`${testIdBase}-root`}
      data-target-kind={targetKind || "all"}
      data-target-id={targetId || ""}
    >
      {advisor && (
        <EditorialRule label="Client + advisor comments" data-testid={`${testIdBase}-rule`} />
      )}

      <div className="space-y-2">
        {loading && (
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
            Loading comments…
          </p>
        )}
        {!loading && visible.length === 0 && (
          <p
            className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]"
            data-testid={`${testIdBase}-empty`}
          >
            {advisor
              ? "No comments yet — clients will see a comment button on every section, day, and hotel."
              : "Have a question or note on this part of the proposal? Share it below — your advisor will see it."}
          </p>
        )}
        {visible.map((c) => (
          <div
            key={c.id}
            className={`rounded-md border p-3 ${
              c.status === "resolved"
                ? "border-[rgba(0,0,0,0.05)] bg-[hsl(var(--background-paper))]/40"
                : "border-[hsl(var(--accent-gold))]/30 bg-white"
            }`}
            data-testid={`${testIdBase}-item-${c.id}`}
            data-status={c.status}
          >
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
                {c.author_kind === "client" ? "Client" : "Advisor"} ·{" "}
                {c.author_name || "—"} · {fmtDate(c.created_at)}
              </p>
              {advisor && c.target_kind && (
                <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                  {TARGET_KIND_LABEL[c.target_kind] || c.target_kind}
                  {c.target_label ? ` · ${c.target_label}` : ""}
                </p>
              )}
            </div>
            <p
              className="mt-1.5 font-serif text-[14px] leading-relaxed text-[hsl(var(--foreground))] whitespace-pre-wrap"
              data-testid={`${testIdBase}-body-${c.id}`}
            >
              {c.body}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {advisor ? (
                <>
                  {c.status === "open" ? (
                    <button
                      type="button"
                      onClick={() => resolve(c.id)}
                      disabled={busyId === c.id}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[hsl(var(--accent-gold))]/40 hover:bg-[hsl(var(--accent-gold))]/10 font-mono text-[10px] uppercase tracking-[0.18em]"
                      data-testid={`${testIdBase}-resolve-${c.id}`}
                    >
                      <Check className="size-3" strokeWidth={1.5} />
                      Resolve
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => reopen(c.id)}
                      disabled={busyId === c.id}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[rgba(0,0,0,0.1)] hover:border-[hsl(var(--accent-gold))]/40 font-mono text-[10px] uppercase tracking-[0.18em]"
                      data-testid={`${testIdBase}-reopen-${c.id}`}
                    >
                      Reopen
                    </button>
                  )}
                  {!c.linked_smart_action_id && c.status === "open" && (
                    <button
                      type="button"
                      onClick={() => convert(c.id)}
                      disabled={busyId === c.id}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[hsl(var(--accent-gold))]/40 hover:bg-[hsl(var(--accent-gold))]/10 font-mono text-[10px] uppercase tracking-[0.18em]"
                      data-testid={`${testIdBase}-convert-${c.id}`}
                    >
                      {busyId === c.id ? (
                        <Loader2 className="size-3 animate-spin" strokeWidth={1.5} />
                      ) : (
                        <Zap className="size-3" strokeWidth={1.5} />
                      )}
                      Smart Action
                    </button>
                  )}
                  {c.linked_smart_action_id && (
                    <span
                      className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]"
                      data-testid={`${testIdBase}-linked-action-${c.id}`}
                    >
                      Linked to Smart Action
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => remove(c.id)}
                    disabled={busyId === c.id}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[hsl(var(--foreground-muted))] hover:text-red-700 hover:bg-red-50 font-mono text-[10px] uppercase tracking-[0.18em]"
                    data-testid={`${testIdBase}-delete-${c.id}`}
                  >
                    <Trash2 className="size-3" strokeWidth={1.5} />
                    Delete
                  </button>
                </>
              ) : (
                c.author_kind === "client" && c.status === "open" && (
                  <button
                    type="button"
                    onClick={() => remove(c.id)}
                    disabled={busyId === c.id}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[hsl(var(--foreground-muted))] hover:text-red-700 hover:bg-red-50 font-mono text-[10px] uppercase tracking-[0.18em]"
                    data-testid={`${testIdBase}-delete-${c.id}`}
                  >
                    <Trash2 className="size-3" strokeWidth={1.5} />
                    Withdraw
                  </button>
                )
              )}
            </div>
          </div>
        ))}
      </div>

      {!advisor && (
        <div
          className="rounded-md border border-[rgba(0,0,0,0.08)] bg-white p-3"
          data-testid={`${testIdBase}-composer`}
        >
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={2}
            placeholder={
              targetLabel
                ? `Share a thought on "${targetLabel}"…`
                : "Share a thought or question…"
            }
            className="w-full px-3 py-2 rounded border border-[rgba(0,0,0,0.08)] bg-white font-serif text-[14px]"
            data-testid={`${testIdBase}-input`}
          />
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !body.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--accent-gold))]/20 hover:bg-[hsl(var(--accent-gold))]/30 disabled:opacity-50 font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground))]"
              data-testid={`${testIdBase}-submit`}
            >
              {submitting ? (
                <Loader2 className="size-3 animate-spin" strokeWidth={1.5} />
              ) : (
                <Send className="size-3" strokeWidth={1.5} />
              )}
              Share
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function ProposalCommentsToggle({ tripId, targetKind, targetId, targetLabel, testIdBase = "proposal-comments" }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[rgba(0,0,0,0.08)] hover:border-[hsl(var(--accent-gold))]/40 hover:bg-[hsl(var(--accent-gold))]/10 font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]"
        data-testid={`${testIdBase}-toggle`}
      >
        <MessageSquare className="size-3" strokeWidth={1.5} />
        {open ? "Hide comments" : "Comment on this"}
      </button>
      {open && (
        <div className="mt-2">
          <ProposalComments
            mode="client"
            tripId={tripId}
            targetKind={targetKind}
            targetId={targetId}
            targetLabel={targetLabel}
            testIdBase={testIdBase}
          />
        </div>
      )}
    </div>
  );
}
